import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import * as api from '@/infra/api/client';
import { withSagaFlow, emitSagaPoint } from '@/features/qpl-client/saga-helpers';
import { Contact, BackgroundTask } from '@/infra/types/domain';
import {
  FETCH_CONTACTS, SEARCH_CONTACTS, CREATE_CONTACT,
  ENABLE_CONTACT, DISABLE_CONTACT, ENHANCE_CONTACT, UPDATE_ROLE,
  UPDATE_CONTACT,
  setContacts, setSearchResults, addContact, setLoading,
  setSearchLoading, setError, updateContactEnabled, setContact,
} from './ducks';
import {
  taskUpserted,
  taskRemoved,
  optimisticTaskId,
} from '@/features/background-tasks/ducks';

function* fetchContactsSaga(action: { type: string }): Generator {
  yield call(withSagaFlow, 'contacts.fetch', ['contacts'], action, function* () {
    yield put(setLoading(true));
    try {
      const contacts = (yield call(api.getEnabledContacts)) as Contact[];
      yield* emitSagaPoint('api_getEnabledContacts_ok', { count: contacts.length });
      yield put(setContacts(contacts));
    } catch (e: any) {
      yield put(setError(e.message || 'Failed to fetch contacts'));
      yield* emitSagaPoint('api_getEnabledContacts_err', { message: e?.message });
    }
  });
}

function* searchContactsSaga(action: { type: string; payload: string }): Generator {
  yield call(withSagaFlow, 'contacts.search', ['contacts'], action, function* () {
    yield put(setSearchLoading(true));
    try {
      const results = (yield call(api.searchContacts, action.payload)) as Contact[];
      yield* emitSagaPoint('api_searchContacts_ok', { count: results.length });
      yield put(setSearchResults(results));
    } catch (e: any) {
      yield put(setError(e.message || 'Search failed'));
      yield put(setSearchLoading(false));
      yield* emitSagaPoint('api_searchContacts_err', { message: e?.message });
    }
  });
}

function* createContactSaga(action: { type: string; payload: { name: string; phone?: string; email?: string; role?: string } }): Generator {
  yield call(withSagaFlow, 'contacts.create', ['contacts'], action, function* () {
    try {
      const contact = (yield call(api.createContact, action.payload)) as Contact;
      yield* emitSagaPoint('api_createContact_ok', { contact_id: contact.id });
      yield put(addContact(contact));
    } catch (e: any) {
      yield put(setError(e.message || 'Failed to create contact'));
      yield* emitSagaPoint('api_createContact_err', { message: e?.message });
    }
  });
}

export function* enableContactSaga(action: { type: string; payload: { id: string; role?: string } }): Generator {
  yield call(withSagaFlow, 'contacts.enable', ['contacts'], action, function* () {
    // Optimistic flip: enabled chip flips immediately. Server response carries
    // the full Contact (role, handles, etc.) — replay it via setContact and
    // skip the FETCH_CONTACTS refetch. See feedback_saga_full_delta.
    yield put(updateContactEnabled(action.payload.id, true));
    try {
      const updated = (yield call(api.enableContact, action.payload.id, action.payload.role)) as Contact;
      yield* emitSagaPoint('api_enableContact_ok');
      yield put(setContact(updated));
    } catch (e: any) {
      // Rollback the optimistic flip on failure.
      yield put(updateContactEnabled(action.payload.id, false));
      yield put(setError(e.message || 'Failed to enable contact'));
      yield* emitSagaPoint('api_enableContact_err', { message: e?.message });
    }
  });
}

export function* disableContactSaga(action: { type: string; payload: string }): Generator {
  yield call(withSagaFlow, 'contacts.disable', ['contacts'], action, function* () {
    // Optimistic flip + pext-style server-truth replay; no FETCH_CONTACTS.
    yield put(updateContactEnabled(action.payload, false));
    try {
      const updated = (yield call(api.disableContact, action.payload)) as Contact;
      yield* emitSagaPoint('api_disableContact_ok');
      yield put(setContact(updated));
    } catch (e: any) {
      yield put(updateContactEnabled(action.payload, true));
      yield put(setError(e.message || 'Failed to disable contact'));
      yield* emitSagaPoint('api_disableContact_err', { message: e?.message });
    }
  });
}

function* updateContactSaga(action: { type: string; payload: { id: string; data: any } }): Generator {
  yield call(withSagaFlow, 'contacts.update', ['contacts'], action, function* () {
    try {
      const updated = (yield call(api.updateContact, action.payload.id, action.payload.data)) as Contact;
      yield* emitSagaPoint('api_updateContact_ok');
      yield put(setContact(updated));
    } catch (e: any) {
      yield put(setError(e.message || 'Failed to update contact'));
      yield* emitSagaPoint('api_updateContact_err', { message: e?.message });
    }
  });
}

function* enhanceContactSaga(action: { type: string; payload: string }): Generator {
  yield call(withSagaFlow, 'contacts.enhance', ['contacts', 'backgroundTasks'], action, function* () {
    const contactId = action.payload;
    const subjectId = contactId;
    const optimisticId = optimisticTaskId('enhance_contact', subjectId);
    // Optimistic entry: flips the Enhance button to "Enhancing..." immediately
    // on click. The real task.started WS broadcast (typically 300–800ms later,
    // after Temporal schedules + RecordTaskStart activity runs) supersedes this
    // row via the TASK_UPSERTED reducer's (task_type, subject_id) dedup.
    const now = new Date().toISOString();
    const optimistic: BackgroundTask = {
      id: optimisticId,
      workflow_id: optimisticId,
      task_type: 'enhance_contact',
      status: 'running',
      triggered_by: '',
      subject_type: 'contact',
      subject_id: subjectId,
      result_summary: {},
      deeplink_params: {},
      started_at: now,
      created_at: now,
      updated_at: now,
    };
    yield put(taskUpserted(optimistic));
    try {
      yield call(api.enhanceContact, contactId);
      yield* emitSagaPoint('api_enhanceContact_ok');
    } catch (e: any) {
      // Roll back the optimistic entry so the button returns to "Enhance".
      yield put(taskRemoved(optimisticId));
      yield put(setError(e.message || 'Failed to enhance contact'));
      yield* emitSagaPoint('api_enhanceContact_err', { message: e?.message });
    }
  });
}

export function* updateRoleSaga(action: { type: string; payload: { id: string; role: string } }): Generator {
  yield call(withSagaFlow, 'contacts.update_role', ['contacts'], action, function* () {
    // Pext-style: server response is the full Contact post-mutation; replay
    // via setContact (full replace, since proto3 omits cleared optionals) and
    // skip the FETCH_CONTACTS refetch.
    try {
      const updated = (yield call(api.updateContactRole, action.payload.id, action.payload.role)) as Contact;
      yield* emitSagaPoint('api_updateContactRole_ok');
      yield put(setContact(updated));
    } catch (e: any) {
      yield put(setError(e.message || 'Failed to update role'));
      yield* emitSagaPoint('api_updateContactRole_err', { message: e?.message });
    }
  });
}

export function* contactsSaga(): Generator {
  yield takeLatest(FETCH_CONTACTS, fetchContactsSaga);
  yield takeLatest(SEARCH_CONTACTS, searchContactsSaga);
  yield takeEvery(CREATE_CONTACT, createContactSaga);
  yield takeEvery(ENABLE_CONTACT, enableContactSaga);
  yield takeEvery(DISABLE_CONTACT, disableContactSaga);
  yield takeEvery(UPDATE_CONTACT, updateContactSaga);
  yield takeEvery(ENHANCE_CONTACT, enhanceContactSaga);
  yield takeEvery(UPDATE_ROLE, updateRoleSaga);
}
